// The task page's storage notices: a banner from 100% for everyone, and from
// 105% a pop-up for workspace admins on page load and when a new photo
// arrives, at most once every 5 minutes. See utils/storageLimit.js for the
// levels; the emails are sent by the 09:00 job in the functions.
import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, AlertTitle, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography,
} from '@mui/material';
import UpgradeContactLink from './UpgradeContactLink';
import { LEVEL, claimPopupSlot, formatGb } from '../utils/storageLimit';

const HOUSEKEEPING = 'free up space by deleting tasks you no longer need, or by setting auto-delete on a task template.';

function WaysOut({ isAdmin, workspaceName }) {
  if (!isAdmin) {
    return ' Your workspace admin can arrange more storage or free up space.';
  }
  return (
    <>
      {' '}
      <UpgradeContactLink reason="storage" workspaceName={workspaceName}>Ask TrakLog for more storage</UpgradeContactLink>
      {`, or ${HOUSEKEEPING}`}
    </>
  );
}

export function StorageLimitBanner({ storage, workspaceName, isAdmin, isSystemAdmin, sx }) {
  if (!storage?.showBanner) return null;
  const name = workspaceName || 'This workspace';
  const usage = `${name} is using ${formatGb(storage.usedBytes)} of its ${formatGb(storage.limitBytes)} (${storage.percent}%).`;

  if (storage.level === LEVEL.PHOTOS_HIDDEN) {
    return (
      <Alert severity="error" sx={sx} data-testid="storage-limit-banner">
        <AlertTitle>Photos are hidden on the task pages</AlertTitle>
        {usage} Every photo is saved, operators keep uploading, and downloading a task shows its
        photos. They return as soon as usage drops below 110%.
        {isSystemAdmin && ' You are a system admin, so you still see them.'}
        <WaysOut isAdmin={isAdmin} workspaceName={workspaceName} />
      </Alert>
    );
  }
  return (
    <Alert severity="warning" sx={sx} data-testid="storage-limit-banner">
      <AlertTitle>Storage is full</AlertTitle>
      {usage} Operators can keep working and every photo is saved. At 110%
      ({formatGb(storage.photosHiddenAtBytes)}), photos will no longer show on the task pages.
      <WaysOut isAdmin={isAdmin} workspaceName={workspaceName} />
    </Alert>
  );
}

/**
 * The admin's pop-up. Shown when the page opens and each time `arrivals`
 * goes up (a new photo reached the list), but never twice within 5 minutes
 * in the same browser.
 */
export function StorageLimitPopup({ storage, workspaceName, subscriberId, arrivals = 0 }) {
  const [open, setOpen] = useState(false);
  const lastArrivals = useRef(null);
  const show = Boolean(storage?.showPopup);

  useEffect(() => {
    if (!show) return;
    // First run is the page load; later runs are new photos.
    if (lastArrivals.current !== null && arrivals <= lastArrivals.current) return;
    lastArrivals.current = arrivals;
    if (claimPopupSlot(subscriberId)) setOpen(true);
  }, [show, arrivals, subscriberId]);

  if (!show) return null;
  const name = workspaceName || 'This workspace';
  const hidden = storage.level === LEVEL.PHOTOS_HIDDEN;

  return (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth data-testid="storage-limit-popup">
      <DialogTitle>{hidden ? 'Photos are hidden on the task pages' : 'Storage is over its limit'}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          {name} is using {formatGb(storage.usedBytes)} of its {formatGb(storage.limitBytes)} ({storage.percent}%).
        </Typography>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          {hidden
            ? 'Photos no longer show on the task pages. Every photo is saved, operators keep uploading, and downloading a task shows its photos.'
            : `At 110% (${formatGb(storage.photosHiddenAtBytes)}), photos will no longer show on the task pages. They stay saved, and downloading a task still shows them.`}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Ask TrakLog for more storage, or {HOUSEKEEPING}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)}>Not now</Button>
        <UpgradeContactLink button reason="storage" workspaceName={workspaceName} />
      </DialogActions>
    </Dialog>
  );
}

/** A tile's stand-in for a photo the task pages hide at 110%. */
export function PhotosHiddenNote({ count, sx }) {
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      sx={sx}
      data-testid="photos-hidden-note"
    >
      {count ? `${count} photo${count === 1 ? '' : 's'} hidden` : 'Photos hidden'} because storage is
      full. Free up space, or download the task to see them.
    </Typography>
  );
}
